<template>
  <div class="min-h-screen bg-gray-50">
    <AppHeader />
    
    <div class="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <!-- Page Header -->
      <div class="flex items-center justify-between mb-8">
        <div>
          <h1 class="text-2xl font-bold text-gray-900">Invoice</h1>
          <p class="text-gray-600">Order #{{ order?.order_number }}</p>
        </div>
        <router-link 
          :to="`/orders`" 
          class="btn btn-outline"
        >
          ← Back to Orders
        </router-link>
      </div>

      <!-- Invoice Component -->
      <div v-if="order" class="mb-8">
        <InvoiceGenerator 
          :order="order" 
          :payment="payment"
          @download="handleDownload"
          @print="handlePrint"
        />
      </div>

      <!-- Loading State -->
      <div v-else class="text-center py-16">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
        <p class="text-gray-600">Loading invoice...</p>
      </div>
    </div>
    
    <AppFooter />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useOrderStore } from '@/stores/orders'
import { usePaymentStore } from '@/stores/payments'
import { useUIStore } from '@/stores/ui'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import InvoiceGenerator from '@/components/InvoiceGenerator.vue'

const route = useRoute()
const router = useRouter()
const orderStore = useOrderStore()
const paymentStore = usePaymentStore()
const uiStore = useUIStore()

// State
const order = ref(null)
const payment = ref(null)

// Methods
const handleDownload = () => {
  uiStore.showNotification({
    type: 'success',
    title: 'Invoice Downloaded',
    message: 'Your invoice has been downloaded successfully.'
  })
}

const handlePrint = () => {
  uiStore.showNotification({
    type: 'info',
    title: 'Invoice Printed',
    message: 'Your invoice has been sent to the printer.'
  })
}

// Initialize
onMounted(async () => {
  const orderId = route.params.id
  if (orderId) {
    try {
      // Fetch order details
      order.value = await orderStore.fetchOrder(orderId)
      
      // Fetch payment details if order is paid
      if (order.value && ['completed', 'processing'].includes(order.value.status)) {
        const payments = await paymentStore.fetchPayments()
        payment.value = payments.find(p => p.order_id === parseInt(orderId))
      }
    } catch (error) {
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load invoice details.'
      })
      router.push('/orders')
    }
  } else {
    router.push('/orders')
  }
})
</script>
